import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AICommand from "@/pages/AICommand";
import CRM from "@/pages/CRM";
import Operations from "@/pages/Operations";
import Proposals from "@/pages/Proposals";
import Knowledge from "@/pages/Knowledge";
import Settings from "@/pages/Settings";
import Engagement from "@/pages/Engagement";
import Meetings from "@/pages/Meetings";
import Studio from "@/pages/Studio";
import Finance from "@/pages/Finance";
import Automation from "@/pages/Automation";
import Platform from "@/pages/Platform";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Billing from "@/pages/Billing";
import Team from "@/pages/Team";
import Research from "@/pages/Research";
import Sign from "@/pages/Sign";
import Copilot from "@/pages/Copilot";
import Graph from "@/pages/Graph";
import Simulation from "@/pages/Simulation";
import Africa from "@/pages/Africa";
import Marketplace from "@/pages/Marketplace";
import MarketplacePack from "@/pages/MarketplacePack";
import Memory from "@/pages/Memory";
import Reputation from "@/pages/Reputation";
import Diplomacy from "@/pages/Diplomacy";
import Entities from "@/pages/Entities";
import FinOps from "@/pages/FinOps";
import Vault from "@/pages/Vault";
import Agents from "@/pages/Agents";
import Security from "@/pages/Security";
import Grc from "@/pages/Grc";
import Analytics from "@/pages/Analytics";
import Organisation from "@/pages/Organisation";
import Profile from "@/pages/Profile";
import Guide from "@/pages/Guide";
import LiveMeetings from "@/pages/LiveMeetings";

const Protected = ({ children, module }) => {
  const { user, loading, hasModule, billingLoading } = useAuth();
  if (loading || billingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-app)" }}>
        <div className="font-mono text-xs tracking-widest text-slate-500">// AUTHENTICATING SESSION</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (module && !hasModule(module)) {
    return <Navigate to="/billing?gated=1" replace />;
  }
  return <Layout>{children}</Layout>;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#0A111F",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#F8FAFC",
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: "13px",
                borderRadius: 2,
              },
            }}
          />
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            {/* Pricing is public — anyone (signed-in or not) can view */}
            <Route path="/pricing" element={<Pricing />} />
            {/* About is public — marketing/positioning page */}
            <Route path="/about" element={<About />} />
            {/* PUBLIC e-sign page — no auth required, token in URL is the credential */}
            <Route path="/sign/:token" element={<Sign />} />

            <Route path="/" element={<Protected module="dashboard"><Dashboard /></Protected>} />
            <Route path="/ai" element={<Protected module="ai_nexus"><AICommand /></Protected>} />
            <Route path="/crm" element={<Protected module="crm"><CRM /></Protected>} />
            <Route path="/operations" element={<Protected module="operations"><Operations /></Protected>} />
            <Route path="/proposals" element={<Protected module="proposals"><Proposals /></Protected>} />
            <Route path="/knowledge" element={<Protected module="knowledge"><Knowledge /></Protected>} />
            <Route path="/engagement" element={<Protected module="engagement"><Engagement /></Protected>} />
            <Route path="/meetings" element={<Protected module="meetings"><Meetings /></Protected>} />
            <Route path="/studio" element={<Protected module="studio"><Studio /></Protected>} />
            <Route path="/finance" element={<Protected module="finance"><Finance /></Protected>} />
            <Route path="/automation" element={<Protected module="automation"><Automation /></Protected>} />
            <Route path="/platform" element={<Protected module="platform"><Platform /></Protected>} />
            <Route path="/billing" element={<Protected module="billing"><Billing /></Protected>} />
            <Route path="/team" element={<Protected module="team"><Team /></Protected>} />
            <Route path="/research" element={<Protected module="research"><Research /></Protected>} />
            <Route path="/copilot" element={<Protected module="copilot"><Copilot /></Protected>} />
            <Route path="/graph" element={<Protected module="graph"><Graph /></Protected>} />
            <Route path="/simulation" element={<Protected module="simulation"><Simulation /></Protected>} />
            <Route path="/africa" element={<Protected module="africa"><Africa /></Protected>} />
            <Route path="/marketplace" element={<Protected module="marketplace"><Marketplace /></Protected>} />
            <Route path="/marketplace/:packId" element={<Protected module="marketplace"><MarketplacePack /></Protected>} />
            <Route path="/memory" element={<Protected module="memory"><Memory /></Protected>} />
            <Route path="/reputation" element={<Protected module="reputation"><Reputation /></Protected>} />
            <Route path="/diplomacy" element={<Protected module="diplomacy"><Diplomacy /></Protected>} />
            <Route path="/entities" element={<Protected module="entities"><Entities /></Protected>} />
            <Route path="/finops" element={<Protected module="finops"><FinOps /></Protected>} />
            <Route path="/vault" element={<Protected module="vault"><Vault /></Protected>} />
            <Route path="/agents" element={<Protected module="agents_auto"><Agents /></Protected>} />
            <Route path="/security" element={<Protected module="security"><Security /></Protected>} />
            <Route path="/grc" element={<Protected module="grc"><Grc /></Protected>} />
            <Route path="/analytics" element={<Protected module="analytics"><Analytics /></Protected>} />
            <Route path="/organisation" element={<Protected module="organisation"><Organisation /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/guide" element={<Protected><Guide /></Protected>} />
            <Route path="/live-meetings" element={<Protected><LiveMeetings /></Protected>} />
            <Route path="/settings" element={<Protected module="governance"><Settings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
